<template>
  <div :class="['multi-ftr-container', 'card', classes.multiFtrContainer]">
    <div :class="classes.marketCard">
      <CardHeader :eventId="eventId" />

      <div :class="classes.marketNames">
        <div :class="classes.marketNamesWrapper">
          <DynamicText
            v-for="name in ['Moneyline', 'Spread', 'Totals']"
            :id="marketBranding?.marketNameFont"
            :key="name"
            :class="classes.marketTitle"
            tag="span"
            styleGroup="Bet Market"
            :get="marketNameStyle.get"
            :set="marketNameStyle.set"
            class="market-name"
            >{{ name }}</DynamicText
          >
        </div>
      </div>

      <div :class="classes.team">
        <SingleCompetitor
          :eventId="eventId"
          team="away"
          :class="classes.teamName"
          class="away-team"
        />

        <div :class="classes.markets">
          <Button
            v-for="(market, index) in homeAwayOptions"
            :key="market?.marketId || index"
            :stagger="index"
            :class="classes.marketButton"
            :optionId="market?.options[0]"
            :marketId="market?.marketId"
            class="home-button"
          />
        </div>
      </div>

      <div :class="classes.divider">
        <DynamicText
          tag="div"
          styleGroup="Event Name"
          :get="fixtureNameStyle.get"
          :set="fixtureNameStyle.set"
          class="vs"
        >
          @</DynamicText
        >
      </div>

      <div :class="classes.team">
        <SingleCompetitor
          :eventId="eventId"
          team="home"
          :class="classes.teamName"
          class="home-team"
        />

        <div :class="classes.markets">
          <Button
            v-for="(market, index) in homeAwayOptions"
            :key="market?.marketId || index"
            :stagger="index + 3"
            :class="classes.marketButton"
            :optionId="market?.options[market?.options.length - 1]"
            :marketId="market?.marketId"
            class="away-button"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ComputedRef, defineComponent, PropType } from "vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { AdTemplateMarket, Brand } from "@fresh8/types/src/index";

import Button from "./Button.vue";
import SingleCompetitor from "../../SingleCompetitor.vue";
import DynamicText from "../../DynamicText.vue";
import CardHeader from "../../CardHeader.vue";

import {
  composeEvent,
  composeHomeAwayOptions,
  composeMultiFtrMarkets
} from "@fresh8/ad-data-composition/src/betData";
import composeDate from "@fresh8/ad-data-composition/src/date";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { SportEvent } from "@fresh8/types/src/betData";

export default defineComponent({
  name: "MultiFtrCard",
  components: {
    Button,
    SingleCompetitor,
    DynamicText,
    CardHeader
  },
  props: {
    marketBranding: {
      type: Object as PropType<AdTemplateMarket>,
      required: false,
      default: undefined
    },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const { adTemplateBranding } = injectBrand();
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const classesRef = useMultiFtrCardStyles(adTemplateBranding);

    const marketIds = composeMultiFtrMarkets(props.eventId);
    const homeAwayOptions = composeHomeAwayOptions(marketIds);
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const fixtureStartDate = composeDate(event.value.id);

    return {
      marketIds,
      homeAwayOptions,
      event,
      fixtureStartDate,
      classes: classesRef.value,
      marketNameStyle: {
        get(brand: Brand | undefined) {
          return brand && brand.adTemplate.market.marketNameFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.marketNameFont = value;
        }
      },
      fixtureTimeFont: {
        get(brand: Brand | undefined) {
          return (
            (brand && brand.adTemplate.market.fixtureTimeFont) ||
            (brand && Object.keys(brand.fonts)[0])
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      },
      fixtureSportFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureSportFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureSportFont = value;
        }
      },
      fixtureNameStyle: {
        get: (brand: Brand | undefined) =>
          brand && brand.adTemplate.market.teamNameFont,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});

const useMultiFtrCardStyles = createUseStyles({
  multiFtrContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  marketCard: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    height: "100%",
    border: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    background: props => props.cardBackgroundColor,
    borderRadius: props => `${props.borderRadius}px`,
    paddingBottom: "10px"
  },
  fixtureSport: {
    width: "100%",
    borderBottom: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    display: "flex",
    justifyContent: "space-between",
    fontSize: "10px",
    padding: "0 8px",
    height: "20px",
    boxSizing: "border-box",
    alignItems: "center"
  },
  marketNames: {
    width: "100%",
    height: "25px",
    fontSize: "10px",
    padding: "4px 8px",
    display: "flex",
    justifyContent: "flex-end",
    boxSizing: "border-box"
  },
  marketNamesWrapper: {
    width: "70%",
    display: "flex",
    justifyContent: "space-around",
    margin: "-5px",
    alignItems: "center"
  },
  marketTitle: {
    width: "100%",
    textAlign: "center",
    margin: "5px"
  },
  team: {
    padding: "0 8px",
    boxSizing: "border-box",
    flex: 1,
    display: "flex",
    alignItems: "stretch"
  },
  teamName: {
    flex: 1,
    fontSize: "16px",
    alignSelf: "center",
    paddingRight: "8px"
  },
  markets: {
    display: "flex",
    width: "70%",
    flexDirection: "row",
    justifyContent: "space-around",
    margin: "-5px"
  },
  marketButton: {
    minHeight: "45px",
    flex: 1,
    margin: "5px"
  },
  divider: {
    fontSize: "12px",
    width: "100%",
    position: "relative",
    padding: "10px 8px",
    boxSizing: "border-box",
    "&:after": {
      content: "''",
      position: "absolute",
      right: 8,
      left: 30,
      top: "50%",
      height: "1px",
      borderTop: "1px dotted #dcdddf"
    }
  },
  live: {
    color: "red"
  }
});
</script>
